/**
 * AR Approval Inbox — lists AR Invoices and Receipts with workflow status
 * Submitted or PendingApproval so approvers can act without opening each record.
 *
 * Permission guards:
 *   AR_INVOICE_APPROVE / AR_RECEIPT_APPROVE — shown per entity type.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CheckCircle2, XCircle, Clock, RefreshCw, FileText,
  ChevronLeft, ChevronRight, Inbox, Receipt,
} from 'lucide-react';

import { arInvoiceApi } from '@/api/arInvoice.api';
import { arReceiptApi } from '@/api/arReceipt.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }  from '@/constants/permissions';
import { Spinner }      from '@/components/ui/Spinner';
import { cn }           from '@/utils/cn';
import type { RootState } from '@/state/store';

import type { ArInvoiceListDto }  from '@/types/arInvoice.types';
import type { ArReceiptListDto }  from '@/types/arReceipt.types';
import {
  AR_WORKFLOW_STATUS_LABELS,
  AR_WORKFLOW_STATUS_COLORS,
} from '@/types/arInvoice.types';

// ── Shared status badge for receipts (same palette) ───────────────────────────

const RECEIPT_STATUS_COLORS: Record<string, string> = {
  Draft:           'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  Submitted:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  PendingApproval: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  Approved:        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Rejected:        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Posted:          'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Reversed:        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Cancelled:       'bg-slate-100 dark:bg-slate-800 text-slate-400',
};

const fmt = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtDate = (s: string) => fmt.format(new Date(s));
const fmtAmt  = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Tabs ──────────────────────────────────────────────────────────────────────

type ActiveTab = 'invoices' | 'receipts';

// ── Main Component ────────────────────────────────────────────────────────────

export const ArApprovalInboxPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canApproveInvoice = hasPermission(Permissions.AR_INVOICE_APPROVE);
  const canRejectInvoice  = hasPermission(Permissions.AR_INVOICE_REJECT);
  const canApproveReceipt = hasPermission(Permissions.AR_RECEIPT_APPROVE);
  const canRejectReceipt  = hasPermission(Permissions.AR_RECEIPT_REJECT);

  const session = useSelector((s: RootState) => s.session.session);

  const [activeTab, setActiveTab] = useState<ActiveTab>('invoices');

  // ── Invoices state ────────────────────────────────────────────────────────
  const [invoices,    setInvoices]    = useState<ArInvoiceListDto[]>([]);
  const [invLoading,  setInvLoading]  = useState(false);
  const [invTotal,    setInvTotal]    = useState(0);
  const [invPage,     setInvPage]     = useState(1);

  // ── Receipts state ────────────────────────────────────────────────────────
  const [receipts,    setReceipts]    = useState<ArReceiptListDto[]>([]);
  const [rctLoading,  setRctLoading]  = useState(false);
  const [rctTotal,    setRctTotal]    = useState(0);
  const [rctPage,     setRctPage]     = useState(1);

  const pageSize = 20;

  // ── Load invoices ─────────────────────────────────────────────────────────
  const loadInvoices = useCallback(async (p: number) => {
    setInvLoading(true);
    try {
      const base = {
        pageNumber:     p,
        pageSize,
        sortBy:         'invoiceDate',
        sortDescending: true,
        locationId:     session?.locationId   ?? undefined,
        costCenterId:   session?.costCenterId ?? undefined,
      };
      const [r1, r2] = await Promise.all([
        arInvoiceApi.getList({ ...base, workflowStatus: 'Submitted' }),
        arInvoiceApi.getList({ ...base, workflowStatus: 'PendingApproval' }),
      ]);
      const merged = [
        ...(r1.data?.data?.items ?? []),
        ...(r2.data?.data?.items ?? []),
      ].sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime());
      setInvoices(merged);
      setInvTotal((r1.data?.data?.totalCount ?? 0) + (r2.data?.data?.totalCount ?? 0));
    } catch (err) {
      console.error('Failed to load AR invoice inbox', err);
    } finally {
      setInvLoading(false);
    }
  }, [session]);

  // ── Load receipts ─────────────────────────────────────────────────────────
  const loadReceipts = useCallback(async (p: number) => {
    setRctLoading(true);
    try {
      const base = {
        pageNumber:     p,
        pageSize,
        sortBy:         'receiptDate',
        sortDescending: true,
        locationId:     session?.locationId   ?? undefined,
        costCenterId:   session?.costCenterId ?? undefined,
      };
      const [r1, r2] = await Promise.all([
        arReceiptApi.getList({ ...base, workflowStatus: 'Submitted' }),
        arReceiptApi.getList({ ...base, workflowStatus: 'PendingApproval' }),
      ]);
      const merged = [
        ...(r1.data?.data?.items ?? []),
        ...(r2.data?.data?.items ?? []),
      ].sort((a, b) => new Date(b.receiptDate).getTime() - new Date(a.receiptDate).getTime());
      setReceipts(merged);
      setRctTotal((r1.data?.data?.totalCount ?? 0) + (r2.data?.data?.totalCount ?? 0));
    } catch (err) {
      console.error('Failed to load AR receipt inbox', err);
    } finally {
      setRctLoading(false);
    }
  }, [session]);

  useEffect(() => { loadInvoices(invPage); }, [invPage, loadInvoices]);
  useEffect(() => { loadReceipts(rctPage); }, [rctPage, loadReceipts]);

  const invTotalPages = Math.ceil(invTotal / pageSize);
  const rctTotalPages = Math.ceil(rctTotal / pageSize);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Inbox size={20} className="text-blue-600 dark:text-blue-400" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                AR Approval Inbox
              </h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Invoices and receipts awaiting your approval action
            </p>
          </div>
          <button
            onClick={() => { loadInvoices(invPage); loadReceipts(rctPage); }}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Permission notice */}
        {!canApproveInvoice && !canApproveReceipt && !canRejectInvoice && !canRejectReceipt && (
          <div className="mt-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
            You don't currently have permission to approve or reject AR documents.
            Contact your administrator if this is incorrect.
          </div>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div className="flex gap-1 mt-4">
          <button
            onClick={() => setActiveTab('invoices')}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
              activeTab === 'invoices'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300',
            )}
          >
            <FileText size={14} />
            Invoices
            {invTotal > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full">
                {invTotal}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('receipts')}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
              activeTab === 'receipts'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300',
            )}
          >
            <Receipt size={14} />
            Receipts
            {rctTotal > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full">
                {rctTotal}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Invoice Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'invoices' && (
        <>
          <div className="flex-1 overflow-auto">
            {invLoading ? (
              <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
            ) : invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <CheckCircle2 size={40} className="mb-3 opacity-30 text-green-500" />
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  No invoices awaiting approval
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Submitted invoices will appear here.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Invoice #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">CCY</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td
                        className="px-4 py-3 font-mono font-semibold text-blue-700 dark:text-blue-400 cursor-pointer hover:underline"
                        onClick={() => navigate(`/ar/invoices/${inv.id}`)}
                      >
                        {inv.invoiceNumber}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-sm">
                        {fmtDate(inv.invoiceDate)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{inv.customerName}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{inv.customerCode}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 dark:text-white">
                        {fmtAmt(inv.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                        {inv.currencyCode}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <Clock size={13} className="text-blue-500 shrink-0" />
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            AR_WORKFLOW_STATUS_COLORS[inv.workflowStatus],
                          )}>
                            {AR_WORKFLOW_STATUS_LABELS[inv.workflowStatus]}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/ar/invoices/${inv.id}`)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                       bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                                       hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            title="Open invoice to review"
                          >
                            <FileText size={11} /> Review
                          </button>
                          {canApproveInvoice && (
                            <button
                              onClick={() => navigate(`/ar/invoices/${inv.id}`)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-green-600 text-white hover:bg-green-700 transition-colors"
                              title="Open to approve"
                            >
                              <CheckCircle2 size={11} /> Approve
                            </button>
                          )}
                          {canRejectInvoice && (
                            <button
                              onClick={() => navigate(`/ar/invoices/${inv.id}`)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-red-600 text-white hover:bg-red-700 transition-colors"
                              title="Open to reject"
                            >
                              <XCircle size={11} /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Invoice pagination */}
          {invTotalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
              <p className="text-sm text-slate-500 dark:text-gray-400">
                Page {invPage} of {invTotalPages} · {invTotal.toLocaleString()} records
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setInvPage(p => p - 1)} disabled={invPage <= 1}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, invTotalPages) }, (_, i) => {
                  const n = Math.max(1, Math.min(invPage - 2, invTotalPages - 4)) + i;
                  return (
                    <button key={n} onClick={() => setInvPage(n)}
                      className={cn('w-8 h-8 text-sm rounded transition-colors',
                        n === invPage
                          ? 'bg-blue-600 text-white font-semibold'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300')}>
                      {n}
                    </button>
                  );
                })}
                <button onClick={() => setInvPage(p => p + 1)} disabled={invPage >= invTotalPages}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Receipt Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'receipts' && (
        <>
          <div className="flex-1 overflow-auto">
            {rctLoading ? (
              <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
            ) : receipts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <CheckCircle2 size={40} className="mb-3 opacity-30 text-green-500" />
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  No receipts awaiting approval
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Submitted receipts will appear here.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Receipt #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Method</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">CCY</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {receipts.map(rct => (
                    <tr key={rct.id} className="hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td
                        className="px-4 py-3 font-mono font-semibold text-blue-700 dark:text-blue-400 cursor-pointer hover:underline"
                        onClick={() => navigate(`/ar/receipts/${rct.id}`)}
                      >
                        {rct.receiptNumber}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-sm">
                        {fmtDate(rct.receiptDate)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{rct.customerName}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{rct.customerCode}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                        {rct.paymentMethod}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 dark:text-white">
                        {fmtAmt(rct.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                        {rct.currencyCode}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <Clock size={13} className="text-blue-500 shrink-0" />
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            RECEIPT_STATUS_COLORS[rct.workflowStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
                          )}>
                            {rct.workflowStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/ar/receipts/${rct.id}`)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                       bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                                       hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            title="Open receipt to review"
                          >
                            <FileText size={11} /> Review
                          </button>
                          {canApproveReceipt && (
                            <button
                              onClick={() => navigate(`/ar/receipts/${rct.id}`)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-green-600 text-white hover:bg-green-700 transition-colors"
                              title="Open to approve"
                            >
                              <CheckCircle2 size={11} /> Approve
                            </button>
                          )}
                          {canRejectReceipt && (
                            <button
                              onClick={() => navigate(`/ar/receipts/${rct.id}`)}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                         bg-red-600 text-white hover:bg-red-700 transition-colors"
                              title="Open to reject"
                            >
                              <XCircle size={11} /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Receipt pagination */}
          {rctTotalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
              <p className="text-sm text-slate-500 dark:text-gray-400">
                Page {rctPage} of {rctTotalPages} · {rctTotal.toLocaleString()} records
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setRctPage(p => p - 1)} disabled={rctPage <= 1}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, rctTotalPages) }, (_, i) => {
                  const n = Math.max(1, Math.min(rctPage - 2, rctTotalPages - 4)) + i;
                  return (
                    <button key={n} onClick={() => setRctPage(n)}
                      className={cn('w-8 h-8 text-sm rounded transition-colors',
                        n === rctPage
                          ? 'bg-blue-600 text-white font-semibold'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300')}>
                      {n}
                    </button>
                  );
                })}
                <button onClick={() => setRctPage(p => p + 1)} disabled={rctPage >= rctTotalPages}
                  className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
